import { Card, Stack, Button, Typography, CardContent, ListItemText } from '@mui/material';
import { UserLevel, type UserType } from 'src/type/user';
import { useBoolean } from 'src/hooks/use-boolean';
import Iconify from 'src/components/iconify';
import InfoGrid from 'src/components/ui-kit/info-grid';
import { fNumber } from 'src/utils/format-number';
import { convertGold } from 'src/utils/convert';
import { ReactNode } from 'react';
import PositionQuotasTable from '../../components/position-quotas-table';
import QuotasCreateDialog from './quotas-create-dialog';
import OpenedPosition from './opened-position';

type Info = {
  label: string;
  value: ReactNode;
  error?: boolean;
};

type Props = {
  user: UserType;
  rootReload: () => void;
};

export default function Portfolio({ user, rootReload }: Props) {
  const form = useBoolean();

  const infos: Info[] = [
    {
      label: 'เงินฝาก/หลักประกัน',
      value: `${fNumber(user.userWallet?.deposit || 0)} THB`,
    },
    {
      label: 'เงินสด/เครดิต',
      value: `${fNumber(user.userWallet?.credit || 0)} THB`,
    },
  ];

  const sortedUserProducts = [...(user?.userProducts || [])].sort((a, b) => {
    const sequenceA = a.product?.sequence ?? 0;
    const sequenceB = b.product?.sequence ?? 0;
    return sequenceA - sequenceB;
  });

  sortedUserProducts.forEach((up) => {
    if (up.product?.name) {
      infos.push({
        label: up.product?.name || '-',
        value: (
          <ListItemText
            primary={`${fNumber(up?.amount)} กรัม`}
            secondary={`≈ ${fNumber(convertGold(up?.amount, 'weightBaht', up?.product?.isOrnament || false))} บาท`}
            primaryTypographyProps={{
              typography: 'body2',
              noWrap: true,
            }}
            secondaryTypographyProps={{
              component: 'span',
              typography: 'caption',
              noWrap: true,
            }}
          />
        ),
      });
    }
  });

  return (
    <>
      {/* Info */}
      <Card>
        <Stack sx={{ width: { xs: '100%', md: '50%' }, px: 3 }} spacing={3}>
          <InfoGrid infos={infos} />
        </Stack>
      </Card>

      {/* Position quotas */}
      {user.level !== UserLevel.LV0 && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="flex-end" justifyContent="space-between">
                  <Typography variant="subtitle1">ปริมาณที่ซื้อ-ขายได้</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon="solar:pen-bold-duotone" />}
                    onClick={form.onTrue}
                  >
                    แก้ไขปริมาณที่ซื้อ-ขายได้
                  </Button>
                </Stack>
                <PositionQuotasTable user={user} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Opened position */}
      <OpenedPosition user={user} />

      <QuotasCreateDialog
        open={form.value}
        onClose={form.onFalse}
        user={user}
        handleReload={rootReload}
      />
    </>
  );
}
